import React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import Text from '../../../shared/_TBComponents/Text';
import {
  CELL_WIDTH,
  ROW_HEIGHT,
  PREVIEW_ROWS,
  WEEK_COLUMN_WIDTH,
  NOTES_COLUMN_WIDTH,
  PREVIEW_REVIEW_ROWS,
} from '../utils/gridConstants';

const PreviewWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  flex: 1;
  min-height: 0;
`;

const PreviewContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  flex: 1;
  min-height: 0;
  padding: 16px;
  box-sizing: border-box;
  opacity: 0.6;
  pointer-events: none;
`;

const Overlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  z-index: 10;
`;

const OverlayBox = styled.div`
  background: #ffffff;
  border-radius: 12px;
  padding: 32px 48px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  text-align: center;
  font-family: 'Nunito Sans', sans-serif;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.04);
  max-width: 480px;
`;

const PlanGridContainer = styled.div`
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 0 18px 18px 18px;
  box-shadow: 0 16px 32px rgba(15, 23, 42, 0.06);
  width: 100%;
  overflow: hidden;
`;

const PlanWeekHeader = styled.div`
  display: flex;
  border-bottom: 1px solid #EEEEEE;
  background: #FFFFFF;
`;

const PlanWeekCell = styled.div`
  flex: 1;
  min-width: ${CELL_WIDTH}px;
  padding: 8px;
  text-align: center;
  font-size: 16px;
  color: #444444;
  border-right: 1px solid #EEEEEE;
  display: flex;
  flex-direction: column;
  gap: 4px;
  justify-content: center;
  align-items: center;
  background: #FFFFFF;

  &:last-child {
    border-right: none;
  }

  .week-number {
    font-size: 16px;
    font-weight: 700;
    color: #9E9E9E;
    line-height: 1.5;
  }

  .week-date {
    font-size: 16px;
    font-weight: 400;
    color: #444444;
    line-height: 1.5;
  }
`;

const PlanGridBody = styled.div`
  display: flex;
  flex-direction: column;
`;

const PlanGridRow = styled.div`
  display: flex;
  min-height: ${ROW_HEIGHT}px;
  border-bottom: 1px solid #EEEEEE;
  background-color: #ffffff;

  &:last-child {
    border-bottom: none;
  }
`;

const PlanGridCell = styled.div`
  flex: 1;
  min-width: ${CELL_WIDTH}px;
  border-right: 1px solid #eeeeee;
  background-color: transparent;
  box-sizing: border-box;

  &:last-child {
    border-right: none;
  }
`;

const ReviewContainer = styled.div`
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border: 1px solid #EEEEEE;
  width: 100%;
  overflow: hidden;
`;

const ReviewTableHeader = styled.div`
  display: flex;
  width: 100%;
  background: #FFFFFF;
`;

const ReviewWeekHeaderCell = styled.div`
  width: ${WEEK_COLUMN_WIDTH}px;
  min-width: ${WEEK_COLUMN_WIDTH}px;
  padding: 8px;
  background: #FFFFFF;
  border-right: 1px solid #EEEEEE;
  border-bottom: 1px solid #EEEEEE;
  display: flex;
  gap: 8px;
  align-items: center;
  font-size: 16px;
  font-weight: 700;
  color: #444444;
  font-family: 'Nunito Sans', sans-serif;
  line-height: 1.5;
`;

const ReviewBlocksHeaderCell = styled.div`
  flex: 1;
  padding: 8px;
  background: #FFFFFF;
  border-right: 1px solid #EEEEEE;
  border-bottom: 1px solid #EEEEEE;
  display: flex;
  align-items: center;
  font-size: 16px;
  font-weight: 700;
  color: #444444;
  font-family: 'Nunito Sans', sans-serif;
  line-height: 1.5;
`;

const ReviewNotesHeaderCell = styled.div`
  width: ${NOTES_COLUMN_WIDTH}px;
  min-width: ${NOTES_COLUMN_WIDTH}px;
  padding: 8px;
  background: #FFFFFF;
  border-bottom: 1px solid #EEEEEE;
  display: flex;
  align-items: center;
  font-size: 16px;
  font-weight: 700;
  color: #444444;
  font-family: 'Nunito Sans', sans-serif;
  line-height: 1.5;
`;

const ReviewTableBody = styled.div`
  display: flex;
  flex-direction: column;
`;

const ReviewTableRow = styled.div`
  display: flex;
  width: 100%;
  min-height: 56px;
`;

const ReviewWeekCell = styled.div`
  width: ${WEEK_COLUMN_WIDTH}px;
  min-width: ${WEEK_COLUMN_WIDTH}px;
  padding: 8px;
  background: #FFFFFF;
  border-right: 1px solid #EEEEEE;
  border-left: 1px solid #EEEEEE;
  border-top: 1px solid #EEEEEE;
  border-bottom: ${(props) => (props.isLast ? '1px solid #EEEEEE' : '0')};
  display: flex;
  gap: 16px;
  align-items: center;
`;

const ReviewWeekNumber = styled.div`
  color: #444444;
  padding: 0 8px;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.5;
  font-family: 'Nunito Sans', sans-serif;
`;

const ReviewWeekDates = styled.div`
  font-size: 16px;
  font-weight: 400;
  color: #444444;
  line-height: 1.5;
  font-family: 'Nunito Sans', sans-serif;
`;

const ReviewBlocksCell = styled.div`
  flex: 1;
  min-height: 56px;
  padding: 4px;
  background: #FFFFFF;
  border-right: 1px solid #EEEEEE;
  border-top: 1px solid #EEEEEE;
  border-bottom: ${(props) => (props.isLast ? '1px solid #EEEEEE' : '0')};
`;

const ReviewNotesCell = styled.div`
  width: ${NOTES_COLUMN_WIDTH}px;
  min-width: ${NOTES_COLUMN_WIDTH}px;
  min-height: 56px;
  padding: 4px;
  padding-left: 32px;
  background: #FFFFFF;
  border-top: 1px solid #EEEEEE;
  border-bottom: ${(props) => (props.isLast ? '1px solid #EEEEEE' : '0')};
  border-right: 1px solid #EEEEEE;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    left: 8px;
    top: 50%;
    transform: translateY(-50%);
    width: 16px;
    height: 16px;
    background-image: url("data:image/svg+xml,%3Csvg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11.3333 2H4.66667C3.93029 2 3.33333 2.59695 3.33333 3.33333V12.6667C3.33333 13.403 3.93029 14 4.66667 14H11.3333C12.0697 14 12.6667 13.403 12.6667 12.6667V3.33333C12.6667 2.59695 12.0697 2 11.3333 2Z' stroke='%239E9E9E' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3Cpath d='M5.33333 5.33333H10.6667' stroke='%239E9E9E' stroke-width='1.5' stroke-linecap='round'/%3E%3Cpath d='M5.33333 8H10.6667' stroke='%239E9E9E' stroke-width='1.5' stroke-linecap='round'/%3E%3Cpath d='M5.33333 10.6667H8.66667' stroke='%239E9E9E' stroke-width='1.5' stroke-linecap='round'/%3E%3C/svg%3E");
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
  }
`;

// Generate placeholder week labels with proper date rollover
const generateWeekLabels = (count) => {
  const baseDate = new Date(2023, 0, 1); // Jan 1, 2023
  return Array.from({ length: count }, (_, i) => {
    const weekDate = new Date(baseDate);
    weekDate.setDate(baseDate.getDate() + (i * 7));
    const formattedDate = weekDate.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
    return {
      number: `W${i + 1}`,
      date: formattedDate,
    };
  });
};

// Plan Preview Component
const PlanPreview = () => {
  const weeks = generateWeekLabels(10);
  const rows = Array.from({ length: PREVIEW_ROWS }, (_, i) => i + 1);

  return (
    <PlanGridContainer>
      <PlanWeekHeader>
        {weeks.map((week) => (
          <PlanWeekCell key={week.number}>
            <span className='week-number'>{week.number}</span>
            <span className='week-date'>{week.date}</span>
          </PlanWeekCell>
        ))}
      </PlanWeekHeader>
      <PlanGridBody>
        {rows.map((rowNum) => (
          <PlanGridRow key={`row-${rowNum}`}>
            {weeks.map((week) => (
              <PlanGridCell key={`cell-${rowNum}-${week.number}`} />
            ))}
          </PlanGridRow>
        ))}
      </PlanGridBody>
    </PlanGridContainer>
  );
};

const ReviewPreview = () => {
  const weeks = generateWeekLabels(PREVIEW_REVIEW_ROWS);

  return (
    <ReviewContainer>
      <ReviewTableHeader>
        <ReviewWeekHeaderCell>Week</ReviewWeekHeaderCell>
        <ReviewBlocksHeaderCell>Blocks</ReviewBlocksHeaderCell>
        <ReviewNotesHeaderCell>Notes</ReviewNotesHeaderCell>
      </ReviewTableHeader>
      <ReviewTableBody>
        {weeks.map((week, index) => (
          <ReviewTableRow key={week.number}>
            <ReviewWeekCell isLast={index === weeks.length - 1}>
              <ReviewWeekNumber>{week.number}</ReviewWeekNumber>
              <ReviewWeekDates>{week.date}</ReviewWeekDates>
            </ReviewWeekCell>
            <ReviewBlocksCell isLast={index === weeks.length - 1} />
            <ReviewNotesCell isLast={index === weeks.length - 1} />
          </ReviewTableRow>
        ))}
      </ReviewTableBody>
    </ReviewContainer>
  );
};

/**
 * PreviewTable Component
 *
 * Shows a read-only preview of the planner table when no calendar is selected.
 * Uses the mode prop from the parent to toggle between Plan and Review views.
 * Displays an overlay message prompting users to select or create a calendar.
 */
const PreviewTable = ({ mode }) => {
  const overlayMessage = 'Select a calendar to start planning';

  return (
    <PreviewWrapper>
      <PreviewContainer>
        {mode === 'plan' ? <PlanPreview /> : <ReviewPreview />}
      </PreviewContainer>
      <Overlay>
        <OverlayBox>
          <Text
            styledTextProps={{
              fontSize: '18px',
              fontWeight: 600,
              lineHeight: 1.5,
              margin: 0,
            }}
          >
            {overlayMessage}
          </Text>
        </OverlayBox>
      </Overlay>
    </PreviewWrapper>
  );
};

PreviewTable.propTypes = {
  mode: PropTypes.oneOf(['plan', 'review']).isRequired,
};

export default PreviewTable;
